import _ from 'lodash'
import conductor from 'scoreboard/components/conductor'
import appDisplay from 'scoreboard/components/app-display'
import ErrorView from 'scoreboard/components/error-view'
import EmptyState from './empty-state'
import HttpStatusCodes from 'http-status-codes'
import i18n from 'scoreboard/components/i18n'
import {isOffline} from 'scoreboard/components/xhr'
import dialogStack from 'scoreboard/components/dialog-stack'
import {isSpectating} from 'scoreboard/components/spectator'
import noPermission from 'scoreboard/components/no-permission.hbs'
import notFound from 'scoreboard/components/not-found.hbs'
import noPermissionsSpecificMessage from 'scoreboard/components/no-permission-specific-message'

/**
 * Handles errors retrieving contents in the display pane
 *
 * @param {jqXhr} [resp] This is a superset of XMLHttpRequest
 * 	see http://api.jquery.com/jQuery.ajax/#jqXHR
 *
 * @param {Boolean} [options.showEmptyState=true] for authorized errors
 * that do not mandate an empty state, this can optionally be set false.
 * for example: an error as a result of a file download should not clear the view
 *
 * @param {Object} [options.jsonData] If the resp does not have a `responseJSON`
 * (because it is not a jqXhr object), then the json data object can be passed
 * into this option.  This is required if a XMLHttpRequest is used.
 *
 * @param {Function} [options.handler]
 * 	If this function returns a truthy value then the default error handling will be skipped.
 * 	The standard error checks (offline, unauthorized, forbidden, not found)
 * 	will always be applied, regardless of this function being provided.
 *
 * 	@param {Function} [options.forbiddenHandler]
 * 	If provided behaves the same as handler but it applied first to forbidden responses.
 *
 * @deprecated error-handler.js should be used instead when handling errors from
 * components.  This function should only be called by high-level error handlers.
 */
function error(resp, options = {}) {
	const showEmptyState = _.get(options, 'showEmptyState', true)
	const json = _.get(options, 'jsonData', _.get(resp, 'responseJSON'))

	if (!resp || isOffline(resp)) {
		offlineErrorHandler(showEmptyState)
	} else if (resp.status == HttpStatusCodes.UNAUTHORIZED) {
		conductor.login()
	} else if (resp.status == HttpStatusCodes.FORBIDDEN) {
		const handled = _.isFunction(options.forbiddenHandler) && options.forbiddenHandler(resp)
		if (!handled) {
			// https://github.com/SpiderStrategies/Scoreboard/issues/27763#issuecomment-570631350
			// We decided to always show 403's in dialogs for XHRs (e.g. drilldowns)
			// See the issue (and related ones) for more detail
			const dialog = !isSpectating() && appDisplay.isDrilldown()
			if (dialog) {
				appDisplay.dialog(new ErrorView(json))
			} else {
				// #41667 build a specific message, if applicable
				let messageWithLink = noPermissionsSpecificMessage(json)
				appDisplay.display(new EmptyState({ body: noPermission({messageWithLink})}))
			}
		}
	} else if (resp.status == HttpStatusCodes.NOT_FOUND) {
		appDisplay.display(new EmptyState({
			body: notFound(resp.responseJSON || {}),
			notFound: true 	// required for #19648
		}))
	} else if (resp.statusText === 'abort') {
		// if the request was aborted, do nothing
	} else {
		const handled = _.isFunction(options.handler) && options.handler(resp)
		if (!handled) {
			const json = _.get(options, 'jsonData', resp.responseJSON)
			appDisplay.dialog(new ErrorView(json))
			// matt sgarlata 4/25/22: this clears the display pane so  it's
			// blank. I don't see why we'd ever want to do this so commented
			// it out.
			//showEmptyState && appDisplay.display(new EmptyState())
		}
	}
}

// #30354 We want to globally handle when we are offline or the server
// is otherwise unavailable
let offlineView // Need to account for multiple invocations per view
function offlineErrorHandler(showEmptyState) {
	if (!offlineView) {
		offlineView = new ErrorView({message: i18n.get('common.error.network')})
		appDisplay.dialog(offlineView)
		dialogStack.peek().on('close', () => {
			offlineView = undefined
		})
		showEmptyState && appDisplay.display(new EmptyState())
	}
}

export default error

error.__filename__ = 'components/display-error-handler.js'
